import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv, faFileExcel, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ExportScopeModal from './ExportScopeModal.js';
import { useExportScope } from './useExportScope.js';

/**
 * Compact CSV / Excel actions for table panel headers.
 *
 * @param {object} props
 * @param {boolean} props.canExport
 * @param {boolean} props.isExporting
 * @param {(opts?: { scope?: string, filename?: string }) => void | Promise<void>} props.onCsv
 * @param {(opts?: { scope?: string, filename?: string }) => void | Promise<void>} props.onExcel
 * @param {(ext: 'csv' | 'xlsx') => string} [props.getResolvedExportFilename]
 * @param {() => { currentPage: number, filtered: number, all: number }} [props.getExportScopeCounts]
 * @param {string} [props.className]
 */
export default function TableExportButtons({
  canExport,
  isExporting,
  onCsv,
  onExcel,
  getResolvedExportFilename,
  getExportScopeCounts,
  className = '',
}) {
  const disabled = !canExport || isExporting;
  const { open, pendingFormat, scopeCounts, openScopeDialog, confirm, cancel } = useExportScope();

  const resolveFilename = (ext) =>
    typeof getResolvedExportFilename === 'function' ? getResolvedExportFilename(ext).trim() : '';

  const handleCsvClick = () => {
    const preview = resolveFilename('csv');
    const counts =
      typeof getExportScopeCounts === 'function'
        ? getExportScopeCounts()
        : { currentPage: 0, filtered: 0, all: 0 };

    openScopeDialog('csv', (scope) => void onCsv({ scope, ...(preview ? { filename: preview } : {}) }), counts);
  };

  const handleExcelClick = () => {
    const preview = resolveFilename('xlsx');
    const counts =
      typeof getExportScopeCounts === 'function'
        ? getExportScopeCounts()
        : { currentPage: 0, filtered: 0, all: 0 };

    openScopeDialog('excel', (scope) => void onExcel({ scope, ...(preview ? { filename: preview } : {}) }), counts);
  };

  return (
    <>
      <div className={`cs-table-export d-flex align-items-center gap-1 ${className}`.trim()} role="group" aria-label="Export table">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary cs-table-export__btn"
          disabled={disabled}
          onClick={handleCsvClick}
          title="Export CSV"
          aria-label="Export CSV"
        >
          {isExporting ? (
            <FontAwesomeIcon icon={faSpinner} spin className="text-secondary" />
          ) : (
            <FontAwesomeIcon icon={faFileCsv} className="text-secondary" />
          )}
          <span className="d-none d-md-inline ms-1">CSV</span>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary cs-table-export__btn"
          disabled={disabled}
          onClick={handleExcelClick}
          title="Export Excel"
          aria-label="Export Excel"
        >
          {isExporting ? (
            <FontAwesomeIcon icon={faSpinner} spin className="text-secondary" />
          ) : (
            <FontAwesomeIcon icon={faFileExcel} className="text-success" />
          )}
          <span className="d-none d-md-inline ms-1">Excel</span>
        </button>
      </div>
      <ExportScopeModal
        show={open}
        onHide={cancel}
        onConfirm={confirm}
        format={pendingFormat}
        scopeCounts={scopeCounts}
      />
    </>
  );
}
